import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Button } from '@/common/ui/button'
import { Form } from '@/common/ui/form'
import { FlexHotGamesForm } from '../../components/FlexHotGamesForm'
import useMutationCreateFlexHotGame from '../../api/hooks/useMutationCreateFlexHotGame'
import { settingsKeys } from '../../api/keys'
import { GameType } from '../../types'

import type { HotGameItemDto } from '../../types'

// Game item schema supporting both fixed and randomized modes
// Using superRefine for conditional validation based on isRandomized flag
const gameItemSchema = z
	.object({
		isRandomized: z.boolean().default(false),
		// Fixed mode fields
		providerId: z.string().optional(),
		gameId: z.string().optional(),
		gameType: z.nativeEnum(GameType).optional(),
		preset: z.string().optional(),
		slotData: z
			.object({
				winProbability: z.number(),
				freeSpinChance: z.number(),
				maxMultiplier: z.number(),
			})
			.optional(),
		nonSlotData: z
			.object({
				bonusAmount: z.number(),
			})
			.optional(),
		// Randomized mode fields
		gameTypes: z.array(z.nativeEnum(GameType)).optional(),
		providerFilter: z
			.object({
				includedProviderIds: z.array(z.string()).optional(),
				excludedProviderIds: z.array(z.string()).optional(),
			})
			.optional(),
		gameFilter: z
			.object({
				includedGameIds: z.array(z.string()).optional(),
				excludedGameIds: z.array(z.string()).optional(),
			})
			.optional(),
		presetFilter: z
			.object({
				includedPresets: z.array(z.string()).optional(),
				excludedPresets: z.array(z.string()).optional(),
			})
			.optional(),
		slotDataRange: z
			.object({
				winProbabilityMin: z.number(),
				winProbabilityMax: z.number(),
				freeSpinChanceMin: z.number(),
				freeSpinChanceMax: z.number(),
				maxMultiplierMin: z.number(),
				maxMultiplierMax: z.number(),
			})
			.optional(),
		nonSlotDataRange: z
			.object({
				bonusAmountMin: z.number(),
				bonusAmountMax: z.number(),
			})
			.optional(),
	})
	.superRefine((data, ctx) => {
		if (data.isRandomized) {
			// ===== RANDOMIZED MODE VALIDATION =====
			// Require at least one game type
			if (!data.gameTypes || data.gameTypes.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณาเลือกประเภทเกมอย่างน้อย 1 ประเภท',
					path: ['gameTypes'],
				})
				return // Stop further validation if no game types
			}

			const hasSlotType = data.gameTypes.includes(GameType.SLOT)
			const hasNonSlotType = data.gameTypes.some((t) => t !== GameType.SLOT)

			// Validate filter mutual exclusion
			if (
				data.providerFilter?.includedProviderIds?.length &&
				data.providerFilter?.excludedProviderIds?.length
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'ไม่สามารถใช้ทั้ง "รวมเฉพาะ" และ "รวมทั้งหมดยกเว้น" พร้อมกันได้',
					path: ['providerFilter'],
				})
			}

			if (
				data.gameFilter?.includedGameIds?.length &&
				data.gameFilter?.excludedGameIds?.length
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'ไม่สามารถใช้ทั้ง "รวมเฉพาะ" และ "รวมทั้งหมดยกเว้น" พร้อมกันได้',
					path: ['gameFilter'],
				})
			}

			if (
				data.presetFilter?.includedPresets?.length &&
				data.presetFilter?.excludedPresets?.length
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'ไม่สามารถใช้ทั้ง "รวมเฉพาะ" และ "รวมทั้งหมดยกเว้น" พร้อมกันได้',
					path: ['presetFilter'],
				})
			}

			// Only validate slotDataRange if SLOT type is selected
			if (hasSlotType && data.slotDataRange) {
				// Win Probability validation
				if (
					data.slotDataRange.winProbabilityMin < 0 ||
					data.slotDataRange.winProbabilityMin > 100
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Win Probability Min ต้องอยู่ระหว่าง 0-100',
						path: ['slotDataRange', 'winProbabilityMin'],
					})
				}
				if (
					data.slotDataRange.winProbabilityMax < 0 ||
					data.slotDataRange.winProbabilityMax > 100
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Win Probability Max ต้องอยู่ระหว่าง 0-100',
						path: ['slotDataRange', 'winProbabilityMax'],
					})
				}
				if (
					data.slotDataRange.winProbabilityMin >
					data.slotDataRange.winProbabilityMax
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Win Probability Min ต้องน้อยกว่าหรือเท่ากับ Max',
						path: ['slotDataRange', 'winProbabilityMin'],
					})
				}

				// Free Spin Chance validation
				if (
					data.slotDataRange.freeSpinChanceMin < 0 ||
					data.slotDataRange.freeSpinChanceMin > 100
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Free Spin Chance Min ต้องอยู่ระหว่าง 0-100',
						path: ['slotDataRange', 'freeSpinChanceMin'],
					})
				}
				if (
					data.slotDataRange.freeSpinChanceMax < 0 ||
					data.slotDataRange.freeSpinChanceMax > 100
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Free Spin Chance Max ต้องอยู่ระหว่าง 0-100',
						path: ['slotDataRange', 'freeSpinChanceMax'],
					})
				}
				if (
					data.slotDataRange.freeSpinChanceMin >
					data.slotDataRange.freeSpinChanceMax
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Free Spin Chance Min ต้องน้อยกว่าหรือเท่ากับ Max',
						path: ['slotDataRange', 'freeSpinChanceMin'],
					})
				}

				// Max Multiplier validation
				if (data.slotDataRange.maxMultiplierMin < 1) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Max Multiplier Min ต้องมากกว่าหรือเท่ากับ 1',
						path: ['slotDataRange', 'maxMultiplierMin'],
					})
				}
				if (data.slotDataRange.maxMultiplierMax < 1) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Max Multiplier Max ต้องมากกว่าหรือเท่ากับ 1',
						path: ['slotDataRange', 'maxMultiplierMax'],
					})
				}
				if (
					data.slotDataRange.maxMultiplierMin >
					data.slotDataRange.maxMultiplierMax
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Max Multiplier Min ต้องน้อยกว่าหรือเท่ากับ Max',
						path: ['slotDataRange', 'maxMultiplierMin'],
					})
				}
			}

			// Only validate nonSlotDataRange if non-SLOT type is selected
			if (hasNonSlotType && data.nonSlotDataRange) {
				if (data.nonSlotDataRange.bonusAmountMin < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Bonus Amount Min ต้องมากกว่าหรือเท่ากับ 0',
						path: ['nonSlotDataRange', 'bonusAmountMin'],
					})
				}
				if (data.nonSlotDataRange.bonusAmountMax < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Bonus Amount Max ต้องมากกว่าหรือเท่ากับ 0',
						path: ['nonSlotDataRange', 'bonusAmountMax'],
					})
				}
				if (
					data.nonSlotDataRange.bonusAmountMin >
					data.nonSlotDataRange.bonusAmountMax
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Bonus Amount Min ต้องน้อยกว่าหรือเท่ากับ Max',
						path: ['nonSlotDataRange', 'bonusAmountMin'],
					})
				}
			}
		} else {
			// ===== FIXED MODE VALIDATION =====
			if (!data.providerId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณาเลือกผู้ให้บริการ',
					path: ['providerId'],
				})
			}
			if (!data.gameId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณาเลือกเกม',
					path: ['gameId'],
				})
			}

			// Only validate based on selected gameType
			if (data.gameType === GameType.SLOT) {
				// Validate slotData for SLOT games
				if (data.slotData) {
					if (
						data.slotData.winProbability < 0 ||
						data.slotData.winProbability > 100
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Win Probability ต้องอยู่ระหว่าง 0-100',
							path: ['slotData', 'winProbability'],
						})
					}
					if (
						data.slotData.freeSpinChance < 0 ||
						data.slotData.freeSpinChance > 100
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Free Spin Chance ต้องอยู่ระหว่าง 0-100',
							path: ['slotData', 'freeSpinChance'],
						})
					}
					if (data.slotData.maxMultiplier < 1) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Max Multiplier ต้องมากกว่าหรือเท่ากับ 1',
							path: ['slotData', 'maxMultiplier'],
						})
					}
				}
			} else {
				// Validate nonSlotData for non-SLOT games
				if (data.nonSlotData) {
					if (data.nonSlotData.bonusAmount < 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Bonus Amount ต้องมากกว่าหรือเท่ากับ 0',
							path: ['nonSlotData', 'bonusAmount'],
						})
					}
				}
			}
		}
	})

const flexHotGameFormSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อ Flex Message'),
	description: z.string().optional(),
	games: z
		.array(gameItemSchema)
		.min(1, 'กรุณาเพิ่มเกมอย่างน้อย 1 เกม')
		.max(10, 'สามารถเพิ่มได้สูงสุด 10 เกม'),
})

type FlexHotGameFormValues = z.infer<typeof flexHotGameFormSchema>

const CreateFlexHotGamePage = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const createFlexHotGameMutation = useMutationCreateFlexHotGame({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: settingsKeys.flexHotGames() })
			toast.success('สร้าง Flex Message สำเร็จ')
			navigate('/operator/settings/flex-hot-games')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการสร้าง Flex Message')
		},
	})

	const form = useForm<FlexHotGameFormValues>({
		resolver: zodResolver(flexHotGameFormSchema),
		defaultValues: {
			name: '',
			description: '',
			games: [
				{
					isRandomized: false,
					providerId: '',
					gameId: '',
					gameType: GameType.SLOT,
					gameTypes: [GameType.SLOT],
					preset: '',
					slotData: {
						winProbability: 0,
						freeSpinChance: 0,
						maxMultiplier: 1,
					},
					nonSlotData: {
						bonusAmount: 0,
					},
					providerFilter: {
						includedProviderIds: [],
						excludedProviderIds: [],
					},
					gameFilter: {
						includedGameIds: [],
						excludedGameIds: [],
					},
					presetFilter: {
						includedPresets: [],
						excludedPresets: [],
					},
					slotDataRange: {
						winProbabilityMin: 0,
						winProbabilityMax: 100,
						freeSpinChanceMin: 0,
						freeSpinChanceMax: 100,
						maxMultiplierMin: 1,
						maxMultiplierMax: 10000,
					},
					nonSlotDataRange: {
						bonusAmountMin: 0,
						bonusAmountMax: 1000,
					},
				},
			],
		},
	})

	const onSubmit = (values: FlexHotGameFormValues) => {
		// Transform games data to match API schema
		const games: HotGameItemDto[] = values.games.map((game) => {
			if (game.isRandomized) {
				// Randomized mode - send gameTypes (array), NOT gameType
				const hasSlotType = game.gameTypes?.includes(GameType.SLOT)
				const hasNonSlotType = game.gameTypes?.some((t) => t !== GameType.SLOT)

				// Clean up filters - only include if there are values
				const providerFilter =
					game.providerFilter?.includedProviderIds?.length ||
					game.providerFilter?.excludedProviderIds?.length
						? {
								includedProviderIds: game.providerFilter.includedProviderIds
									?.length
									? game.providerFilter.includedProviderIds
									: undefined,
								excludedProviderIds: game.providerFilter.excludedProviderIds
									?.length
									? game.providerFilter.excludedProviderIds
									: undefined,
							}
						: undefined

				const gameFilter =
					game.gameFilter?.includedGameIds?.length ||
					game.gameFilter?.excludedGameIds?.length
						? {
								includedGameIds: game.gameFilter.includedGameIds?.length
									? game.gameFilter.includedGameIds
									: undefined,
								excludedGameIds: game.gameFilter.excludedGameIds?.length
									? game.gameFilter.excludedGameIds
									: undefined,
							}
						: undefined

				const presetFilter =
					game.presetFilter?.includedPresets?.length ||
					game.presetFilter?.excludedPresets?.length
						? {
								includedPresets: game.presetFilter.includedPresets?.length
									? game.presetFilter.includedPresets
									: undefined,
								excludedPresets: game.presetFilter.excludedPresets?.length
									? game.presetFilter.excludedPresets
									: undefined,
							}
						: undefined

				return {
					isRandomized: true,
					gameTypes: game.gameTypes, // Send array of game types
					providerFilter,
					gameFilter,
					presetFilter,
					slotDataRange: hasSlotType ? game.slotDataRange : undefined,
					nonSlotDataRange: hasNonSlotType ? game.nonSlotDataRange : undefined,
				} as HotGameItemDto
			} else {
				// Fixed mode - send gameType (single), NOT gameTypes
				return {
					isRandomized: false,
					gameId: game.gameId,
					gameType: game.gameType, // Send single game type
					preset: game.preset || undefined,
					slotData: game.gameType === GameType.SLOT ? game.slotData : undefined,
					nonSlotData:
						game.gameType !== GameType.SLOT ? game.nonSlotData : undefined,
				} as HotGameItemDto
			}
		})

		createFlexHotGameMutation.mutate({
			name: values.name,
			description: values.description || undefined,
			games,
		})
	}

	return (
		<PageContainer>
			<div className="mx-auto max-w-4xl space-y-6">
				<div>
					<h1 className="text-3xl font-bold">สร้าง Flex Message</h1>
					<p className="mt-2 text-muted-foreground">
						สร้าง Flex Message สำหรับส่งโฆษณาเกม
					</p>
				</div>

				{/* Example Image Section */}
				<div className="rounded-lg border bg-card p-6">
					<h2 className="mb-3 text-lg font-semibold">ตัวอย่าง Flex Message</h2>
					<p className="mb-4 text-sm text-muted-foreground">
						ตัวอย่างรูปแบบ Flex Message ที่จะถูกส่งไปยังผู้ใช้
					</p>
					<div className="flex justify-center gap-4 rounded-lg bg-muted/50 p-4">
						<img
							src="https://image.panda-storage.com/general/slot.webp"
							alt="ตัวอย่าง Flex Message"
							className="max-h-[300px] w-auto rounded-lg shadow-md lg:max-h-[400px]"
						/>
						<img
							src="https://image.panda-storage.com/general/non-slot.webp"
							alt="ตัวอย่าง Flex Message"
							className="max-h-[300px] w-auto rounded-lg shadow-md lg:max-h-[400px]"
						/>
					</div>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<FlexHotGamesForm
							form={form}
							disabled={createFlexHotGameMutation.isPending}
						/>

						{/* Submit buttons */}
						<div className="flex gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={() => navigate('/operator/settings/flex-hot-games')}
								disabled={createFlexHotGameMutation.isPending}
							>
								ยกเลิก
							</Button>
							<Button
								type="submit"
								disabled={createFlexHotGameMutation.isPending}
							>
								{createFlexHotGameMutation.isPending
									? 'กำลังสร้าง...'
									: 'สร้าง Flex Message'}
							</Button>
						</div>
					</form>
				</Form>
			</div>
		</PageContainer>
	)
}

const GuardedCreateFlexHotGamePage = withUserPermission(CreateFlexHotGamePage, {
	permissionType: PermissionType.Write,
	permissionKey: [PermissionKeys.MANAGE_SMS],
})

export default GuardedCreateFlexHotGamePage
